import { DashboardFilters } from '@delivery-metrics/shared';
import { useCallback, useEffect, useState } from 'react';

import {
  ShareableState,
  copyShareableURL,
  generateShareableURL,
  getCurrentUrlState,
} from '../utils/shareableUrl';

export interface UseShareableStateOptions {
  autoUpdateUrl?: boolean;
  onStateChange?: (state: ShareableState) => void;
}

export interface UseShareableStateReturn {
  viewState: ShareableState['viewState'];
  updateViewState: (newViewState: Partial<ShareableState['viewState']>) => void;
  generateShareableUrl: (filters: DashboardFilters) => string;
  copyShareableUrl: (filters: DashboardFilters) => Promise<boolean>;
  loadStateFromUrl: () => ShareableState | null;
  resetViewState: () => void;
}

const DEFAULT_VIEW_STATE: ShareableState['viewState'] = {
  currentPage: 1,
  pageSize: 25,
  selectedRows: [],
  expandedSections: [],
  chartZoomLevel: 1,
  drillDownPath: [],
};

/**
 * Hook for managing shareable dashboard state
 */
export function useShareableState(
  options: UseShareableStateOptions = {}
): UseShareableStateReturn {
  const { autoUpdateUrl = true, onStateChange } = options;

  const [viewState, setViewState] = useState<ShareableState['viewState']>(
    () => {
      // Try to load initial state from URL
      const urlState = getCurrentUrlState();
      return urlState?.viewState || DEFAULT_VIEW_STATE;
    }
  );

  // Update view state and optionally sync with URL
  const updateViewState = useCallback(
    (newViewState: Partial<ShareableState['viewState']>) => {
      setViewState(prev => {
        const updated = { ...prev, ...newViewState };

        // Notify about state change
        if (onStateChange) {
          onStateChange({
            filters: {} as DashboardFilters, // Will be provided by caller
            viewState: updated,
            timestamp: Date.now(),
          });
        }

        return updated;
      });
    },
    [onStateChange]
  );

  // Generate shareable URL with current view state
  const generateShareableUrl = useCallback(
    (filters: DashboardFilters): string => {
      return generateShareableURL(filters, viewState);
    },
    [viewState]
  );

  // Copy shareable URL to clipboard
  const copyShareableUrl = useCallback(
    async (filters: DashboardFilters): Promise<boolean> => {
      return await copyShareableURL(filters, viewState);
    },
    [viewState]
  );

  // Load state from current URL
  const loadStateFromUrl = useCallback((): ShareableState | null => {
    const urlState = getCurrentUrlState();
    if (urlState) {
      setViewState(urlState.viewState);
    }
    return urlState;
  }, []);

  // Reset view state to defaults
  const resetViewState = useCallback(() => {
    setViewState(DEFAULT_VIEW_STATE);
  }, []);

  // Auto-update URL when view state changes (if enabled)
  useEffect(() => {
    if (autoUpdateUrl && Object.keys(viewState).length > 0) {
      // We need filters to update URL, but this hook doesn't manage filters
      // The parent component should handle URL updates when filters change
      // This is just for view state changes that don't require filter updates
    }
  }, [viewState, autoUpdateUrl]);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlState = getCurrentUrlState();
      if (urlState) {
        setViewState(urlState.viewState);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return {
    viewState,
    updateViewState,
    generateShareableUrl,
    copyShareableUrl,
    loadStateFromUrl,
    resetViewState,
  };
}

/**
 * Hook specifically for managing drill-down navigation state
 */
export function useDrillDownNavigation() {
  const { viewState, updateViewState } = useShareableState();

  const drillDownPath = viewState.drillDownPath || [];

  const drillDown = useCallback(
    (level: string, data?: any) => {
      const newPath = [...drillDownPath, level];
      updateViewState({
        drillDownPath: newPath,
        drillDownData: data,
      });
    },
    [drillDownPath, updateViewState]
  );

  const drillUp = useCallback(
    (levels: number = 1) => {
      const newPath = drillDownPath.slice(0, -levels);
      updateViewState({
        drillDownPath: newPath,
        drillDownData: undefined,
      });
    },
    [drillDownPath, updateViewState]
  );

  const resetDrillDown = useCallback(() => {
    updateViewState({
      drillDownPath: [],
      drillDownData: undefined,
    });
  }, [updateViewState]);

  return {
    drillDownPath,
    currentLevel: drillDownPath[drillDownPath.length - 1] || 'overview',
    drillDown,
    drillUp,
    resetDrillDown,
    canDrillUp: drillDownPath.length > 0,
  };
}
